import {Injectable} from "@angular/core";
import {Subject} from 'rxjs/Subject';

import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
    CognitoUserSession
} from 'amazon-cognito-identity-js';

import {config} from './config';

import * as AWS from 'aws-sdk';
import * as _ from 'lodash';
import { Site } from "../api/model/site";
import { Router } from "@angular/router";

declare let apigClientFactory: any;

const REGION = 'eu-west-1';
const USER_POOL_ID = 'eu-west-1_ASpJYc3mg';
const CLIENT_ID = '73vk3cbmodijbb4v0v6nclsbuo';

const IDENTITY_POOL_ID = 'eu-west-1:059f5535-3b7b-456f-9e91-997fc84a2137';
const USER_POOL_IDP = 'cognito-idp.' + REGION + '.amazonaws.com/' + USER_POOL_ID;

const poolData: any = {
    UserPoolId: USER_POOL_ID,
    ClientId: CLIENT_ID
};

@Injectable()
export class AwsService {
    private _api: any;
    _AWS: any;

    identityId: string;
    credentials: any;

    private _sites: any = new Subject<any>();
    sites$ = this._sites.asObservable();

    private _user: any = new Subject<any>();
    user$ = this._user.asObservable();

    cognitoUser: CognitoUser;

    config = config;

    constructor(private _router: Router) {
        console.log('[AwsService][constructor]');
        this._AWS = AWS;
        this._AWS.config.region = REGION;
    }

    get api(): any {
        if (this.expired) {
            console.log('[AwsService][api]expired');
            this._router.navigate(['/']);
        }
        return this._api;
    }

    get AWS(): any {
        return this._AWS;
    }

    get expired(): boolean {
        return (!this._AWS.config.credentials || this._AWS.config.credentials.expired);
    }

    async updateSites() {
        let sites: Site[] = (await this.api.sitesGet()).data.items;
        this._sites.next(sites);
    }

    async authenticate(email: string, password: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const authData: any = {
                Email: email,
                Password: password
            };
            const authenticationDetails = new AuthenticationDetails(authData);
            const userPool = new CognitoUserPool(poolData);
            const userData: any = {
                Username: email,
                Pool: userPool
            };
            this.cognitoUser = new CognitoUser(userData);
            this.cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result) => {
                    this._AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                        IdentityPoolId: IDENTITY_POOL_ID,
                        Logins: {
                            [USER_POOL_IDP]: result.getIdToken().getJwtToken()
                        }
                    });

                    this._AWS.config.credentials.refresh(async (err: Error) => {
                        if (err) {
                            console.error('Error', err);
                            reject(false);
                        } else {
                            this.identityId = this._AWS.config.credentials.identityId;
                            this.config.identityId = this._AWS.config.credentials.identityId;
                            this.credentials = {
                                accessKeyId: this._AWS.config.credentials.accessKeyId,
                                secretAccessKey: this._AWS.config.credentials.secretAccessKey,
                                sessionToken: this._AWS.config.credentials.sessionToken,
                                region: this._AWS.config.region
                            };

                            this._api = apigClientFactory.newClient({
                                accessKey: this.credentials.accessKeyId,
                                secretKey: this.credentials.secretAccessKey,
                                sessionToken: this.credentials.sessionToken,
                                region: this.credentials.region
                            });

                            let user = (await this.api.usersIdGet({id: this.identityId})).data;
                            if (_.isEmpty(user)) {
                                user.id = this.identityId;
                                user.enabled = true;
                                user.status = 'CONFIRMED';
                                user.provider = 'COGNITO';
                                const attrs: CognitoUserAttribute[] = await this.getUserAttributes(this.cognitoUser);
                                let att: any = _.find(attrs, {Name: 'email'});
                                if (att) user.email = att.Value;
                                att = _.find(attrs, {Name: 'name'});
                                if (att) user.name = att.Value;
                                att = _.find(attrs, {Name: 'picture'});
                                if (att) user.picture = att.Value;
                                user.roles = ['users'];
                                user = (await this.api.usersPost({}, user)).data;
                            }
                            this._user.next(user);
                            await this.updateSites();
                            resolve(true);
                        }
                    });
                },
                onFailure: err => {
                    console.error('Error', err);
                    reject(false);
                }
            });
        });
    }

    getUserAttributes(cognitoUser: CognitoUser): Promise<CognitoUserAttribute[]> {
        return new Promise<CognitoUserAttribute[]>((resolve, reject) => {
            cognitoUser.getUserAttributes((err: Error, result: CognitoUserAttribute[]) => {
                if (err) reject(err);
                else resolve(result);
            });
        });
    }

    refresh(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const userPool = new CognitoUserPool(poolData);
            this.cognitoUser = userPool.getCurrentUser();
            if (this.cognitoUser != null) {
                this.cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
                    if (err || !session || !session.isValid()) {
                        console.error(err, session);
                        resolve(false);
                    } else {
                        this._AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                            IdentityPoolId: IDENTITY_POOL_ID,
                            Logins: {
                                [USER_POOL_IDP]: session.getIdToken().getJwtToken()
                            }
                        });
                        this._AWS.config.credentials.refresh(async (err: Error) => {
                            if (err) {
                                console.error(err);
                                resolve(false);
                            } else {
                                this.identityId = this._AWS.config.credentials.identityId;
                                this.config.identityId = this._AWS.config.credentials.identityId;
                                this.credentials = {
                                    accessKeyId: this._AWS.config.credentials.accessKeyId,
                                    secretAccessKey: this._AWS.config.credentials.secretAccessKey,
                                    sessionToken: this._AWS.config.credentials.sessionToken,
                                    region: this._AWS.config.region
                                };

                                this._api = apigClientFactory.newClient({
                                    accessKey: this.credentials.accessKeyId,
                                    secretKey: this.credentials.secretAccessKey,
                                    sessionToken: this.credentials.sessionToken,
                                    region: this.credentials.region
                                });

                                const user = (await this.api.usersIdGet({id: this.identityId})).data;
                                console.log(user);
                                if (!user.id) {
                                    try {
                                        await this.singOut();
                                    } catch (err) {}
                                    resolve(false);
                                }
                                this._user.next(user);
                                await this.updateSites();
                                resolve(true);
                            }
                        });
                    }
                });
            } else {
                console.error('Not Current User');
                resolve(false);
            }
        });
    }

    singOut(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const userPool = new CognitoUserPool(poolData);
            this.cognitoUser = userPool.getCurrentUser();
            if (this.cognitoUser != null) {
                this.cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
                    if (err || !session.isValid()) {
                        console.error(err, session.isValid());
                        reject(err);
                    } else {
                        this.cognitoUser.signOut();
                        resolve('ok');
                    }
                });
            } else {
                console.error('Not Current User');
                reject(new Error('Not Current User'));
            }
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/aws.service.ts